<template>
  <DModal
    :showClose="false"
    :isBgOverlay="true"
    :isCloseOnBgClick="false"
    :appendToBody="true"
    :placeInTop="true"
    :autoFocusField="false"
    :isPrintable="true"
    ariaLabel="Kvoter"
    @close="$emit('close')">
    <div class="d-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <div>
          <h2>Kvoter</h2>
          <p class="d-modal__subtitle">
            Restkvote i KMD pr. {{ quotaDateLabel }}. Vælg en dato frem i tiden — f.eks. efter efterårsferien eller før påske — for at se, hvad der stadig skal afholdes inden udløb eller udbetaling.
          </p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__date">
        <label class="d-label">Restkvote pr.</label>
        <DateSelector :date="selectedDate" @changed="onDateChanged"></DateSelector>
      </div>

      <div class="d-modal__select">
        <div v-if="selectAll" class="d-modal__all-selected">
          <span>Alle medarbejdere<span v-if="selectedUserCount > 0"> ({{ selectedUserCount }})</span></span>
          <button type="button" class="d-modal__all-selected__clear" @click="clearSelectAll">
            Fravælg
          </button>
        </div>
        <template v-else>
          <DSelectUsers
            :showTags="true"
            :showCreateButton="false"
            @usersUpdated="onUsersUpdated">
          </DSelectUsers>
          <button
            type="button"
            class="do-bttn d-modal__select-all"
            :disabled="opusEmployments.length === 0"
            @click="selectAllUsers">
            Vælg alle
          </button>
        </template>
      </div>

      <div class="d-modal__toolbar">
        <div class="d-modal__toolbar__filters">
          <DoFiltersFilter
            v-if="quotaTypes.length > 0"
            :title="'Kvotetype'"
            :tags="quotaTypes"
            :selectedTags="selectedQuotaTypes"
            :numberOfSelectedTagsToShow="1"
            :placeholder="'Vælg kvotetype'"
            @select="toggleQuotaType"
            @clear="selectedQuotaTypes = []">
          </DoFiltersFilter>
          <span class="d-modal__count" v-if="hasLoaded">
            Viser {{ filteredRows.length }} af {{ allRows.length }} kvoter
          </span>
        </div>
        <button
          class="do-bttn"
          :class="{'do-bttn--loading': isDownloadingExcel}"
          :disabled="isLoading || isDownloadingExcel || filteredRows.length === 0"
          @click="downloadExcel">
          Download til excel
        </button>
      </div>

      <div class="d-modal__content">
        <p v-if="!hasSelection" class="d-modal__empty">
          Vælg medarbejdere, en gruppe eller alle for at hente kvoter.
        </p>
        <div v-else-if="isLoading" class="d-modal__status">
          <Loader :isSmall="true"></Loader>
          <p v-if="totalCount > 0" class="d-modal__progress">
            Henter kvoter… {{ loadedCount }} / {{ totalCount }} ansættelser
          </p>
        </div>
        <div v-else-if="errorMessage" class="d-modal__error">
          {{ errorMessage }}
        </div>
        <p v-else-if="hasLoaded && allRows.length === 0" class="d-modal__empty">
          Der blev ikke fundet nogen kvoter.
        </p>
        <p v-else-if="hasLoaded && filteredRows.length === 0" class="d-modal__empty">
          Ingen kvoter matcher de valgte filtre.
        </p>
        <table v-else class="do-table">
          <thead>
          <tr>
            <th
              class="th-sorter"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'userName'}"
              @click="setSort('userName')">
              Navn <span class="icon-menu-open"></span>
            </th>
            <th
              v-if="hasGroupLabels"
              class="th-sorter"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'userTagsLabel'}"
              @click="setSort('userTagsLabel')">
              Grupper <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'employmentLabel'}"
              @click="setSort('employmentLabel')">
              Ansættelse <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'quotaType'}"
              @click="setSort('quotaType')">
              Kontotype <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter is-number"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'balance'}"
              @click="setSort('balance')">
              Saldo <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter is-number"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'used'}"
              @click="setSort('used')">
              Brugt <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter is-number"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'difference'}"
              @click="setSort('difference')">
              Forskel <span class="icon-menu-open"></span>
            </th>
            <th
              class="th-sorter"
              role="button"
              tabindex="0"
              :class="{'th-sorter--active': sortBy === 'periodLabel'}"
              @click="setSort('periodLabel')">
              Afholdelsesperiode <span class="icon-menu-open"></span>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in filteredRows" :key="row.id">
            <td>{{ row.userName }}</td>
            <td v-if="hasGroupLabels">{{ row.userTagsLabel }}</td>
            <td>{{ row.employmentLabel }}</td>
            <td>{{ row.quotaType }}</td>
            <td class="is-number">{{ formatDecimal(row.balance) }}</td>
            <td class="is-number">{{ formatDecimal(row.used) }}</td>
            <td class="is-number">{{ formatDecimal(row.difference) }}</td>
            <td>{{ row.periodLabel }}</td>
          </tr>
          </tbody>
        </table>
        <p v-if="warningMessage" class="d-modal__warning">{{ warningMessage }}</p>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../../docendo'
import moment from 'moment'
import DModal from '../../common/DModal.vue'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import DSelectUsers from '../../DSelect/DSelectUsers.vue'
import DateSelector from '../../datepicker/DateSelector.vue'
import Loader from '../../common/Loader.vue'

const CHUNK_SIZE = 10
let XLSX = null

export default {
  name: 'KmdQuotasModal',
  components: {DModal, DoFiltersFilter, DSelectUsers, DateSelector, Loader},
  data () {
    return {
      allRows: [],
      isLoading: false,
      hasLoaded: false,
      fetchGeneration: 0,
      errorMessage: '',
      warningMessage: '',
      loadedCount: 0,
      totalCount: 0,
      selectedUserIds: [],
      selectAll: false,
      selectedDate: moment(),
      selectedQuotaTypes: [],
      sortBy: 'userName',
      sortDir: 'asc',
      isDownloadingExcel: false,
      fetchTimer: null
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    storageKey () {
      return 'kmd-quotas-filters-' + this.schoolId
    },
    locale () {
      return this.$store.getters.locale || 'da'
    },
    effectiveDate () {
      return moment(this.selectedDate).format('YYYY-MM-DD')
    },
    quotaDateLabel () {
      const date = moment(this.effectiveDate, 'YYYY-MM-DD')
      return date.format('DD-MM-YYYY') + ' (uge ' + date.isoWeek() + ')'
    },
    hasSelection () {
      return this.selectAll || this.selectedUserIds.length > 0
    },
    selectedUserCount () {
      if (!this.selectAll) return this.selectedUserIds.length
      const ids = {}
      for (let i = 0; i < this.opusEmployments.length; i++) {
        ids[this.opusEmployments[i].user.id] = true
      }
      return Object.keys(ids).length
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    opusEmployments () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source && emp.source !== 'opus') continue
          if (emp.deleted) continue
          if (emp.end_date && emp.end_date < this.effectiveDate) continue
          out.push({
            user: user,
            employment: emp
          })
        }
      }
      return out
    },
    selectedOpusEmployments () {
      if (this.selectAll) return this.opusEmployments
      if (this.selectedUserIds.length === 0) return []
      const ids = {}
      for (let i = 0; i < this.selectedUserIds.length; i++) {
        ids[this.selectedUserIds[i]] = true
      }
      return this.opusEmployments.filter(pairing => ids[pairing.user.id])
    },
    hasGroupLabels () {
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].userTagsLabel) return true
      }
      return false
    },
    quotaTypes () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].quotaType) set[this.allRows[i].quotaType] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da'))
    },
    filteredRows () {
      let rows = this.allRows.filter(row => {
        if (this.selectedQuotaTypes.length > 0 && this.selectedQuotaTypes.indexOf(row.quotaType) === -1) {
          return false
        }
        return true
      })
      const key = this.sortBy
      const dir = this.sortDir === 'desc' ? -1 : 1
      const numericKeys = {balance: true, used: true, difference: true}
      rows = rows.slice().sort((a, b) => {
        if (numericKeys[key]) {
          const av = this.toNumber(a[key])
          const bv = this.toNumber(b[key])
          if (av === bv) return a.userName.localeCompare(b.userName, 'da')
          return av < bv ? -dir : dir
        }
        const av = a[key] == null ? '' : String(a[key])
        const bv = b[key] == null ? '' : String(b[key])
        const cmp = av.localeCompare(bv, 'da', {sensitivity: 'base'})
        if (cmp !== 0) return cmp * dir
        return a.userName.localeCompare(b.userName, 'da')
      })
      return rows
    }
  },
  watch: {
    selectedQuotaTypes () {
      this.persistFilters()
    },
    opusEmployments () {
      if (this.selectAll) this.scheduleFetch()
    }
  },
  methods: {
    onUsersUpdated (users) {
      const ids = []
      const list = users || []
      for (let i = 0; i < list.length; i++) {
        if (!list[i] || list[i].tag || !list[i].id) continue
        ids.push(list[i].id)
      }
      this.selectedUserIds = ids
      this.scheduleFetch()
    },
    onDateChanged (date) {
      this.selectedDate = date
      this.persistFilters()
      this.scheduleFetch()
    },
    selectAllUsers () {
      this.selectAll = true
      this.selectedUserIds = []
      this.scheduleFetch()
    },
    clearSelectAll () {
      this.selectAll = false
      this.selectedUserIds = []
      this.resetResults()
    },
    scheduleFetch () {
      if (this.fetchTimer) {
        clearTimeout(this.fetchTimer)
        this.fetchTimer = null
      }
      if (!this.hasSelection) {
        this.resetResults()
        return
      }
      const self = this
      this.fetchTimer = setTimeout(function () {
        self.fetchTimer = null
        self.fetchAllQuotas()
      }, 400)
    },
    toggleQuotaType (type) {
      const idx = this.selectedQuotaTypes.indexOf(type)
      if (idx > -1) this.selectedQuotaTypes.splice(idx, 1)
      else this.selectedQuotaTypes.push(type)
    },
    setSort (key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
    },
    persistFilters () {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify({
          selectedQuotaTypes: this.selectedQuotaTypes,
          selectedDate: this.effectiveDate
        }))
      } catch (e) {}
    },
    restoreFilters () {
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (parsed && Array.isArray(parsed.selectedQuotaTypes)) this.selectedQuotaTypes = parsed.selectedQuotaTypes
        if (parsed && parsed.selectedDate && moment(parsed.selectedDate, 'YYYY-MM-DD', true).isValid()) {
          this.selectedDate = moment(parsed.selectedDate, 'YYYY-MM-DD')
        }
      } catch (e) {}
    },
    resetResults () {
      this.fetchGeneration += 1
      this.allRows = []
      this.isLoading = false
      this.hasLoaded = false
      this.errorMessage = ''
      this.warningMessage = ''
      this.loadedCount = 0
      this.totalCount = 0
    },
    toNumber (value) {
      const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'))
      return Number.isFinite(n) ? n : 0
    },
    normalizeEmploymentNumber (value) {
      if (value == null || value === '') return ''
      const asInt = parseInt(String(value).trim(), 10)
      if (!isNaN(asInt)) return String(asInt)
      return String(value).trim()
    },
    quotaEmployeeNumber (quota) {
      if (!quota) return ''
      return quota.EmployeeNumber || quota.PersonnelNumber || quota.EmployeeNo ||
        quota.Medarbejdernummer || quota.EmploymentNumber || quota.EmployeeID || ''
    },
    formatDate (date) {
      if (!date) return ''
      return moment(date, 'YYYY-MM-DD').isValid() ? moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY') : date
    },
    formatDateRange (startDate, endDate) {
      return this.formatDate(startDate) + ' - ' + this.formatDate(endDate)
    },
    formatDecimal (value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return '0'
      }
      const parsedValue = Number(value)
      if (isNaN(parsedValue)) {
        return String(value)
      }
      return parsedValue.toLocaleString(this.locale === 'en' ? 'en-US' : 'da-DK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },
    listQuotas (employmentIds) {
      const self = this
      return new Promise(function (resolve, reject) {
        docendo.listQuotas(
          self.schoolId,
          employmentIds,
          self.effectiveDate,
          function (response) {
            if (response && response.success === false) {
              reject((response.data && response.data.error) || response.error || 'Kunne ikke hente kvoter fra KMD.')
              return
            }
            if (response && response.error) {
              reject(response.error)
              return
            }
            if (response && response.data && response.data.error) {
              reject(response.data.error)
              return
            }
            resolve(self.parseQuotaItems(response))
          },
          function (error) {
            reject((error && error.data && error.data.error) || (error && error.error) || 'Kunne ikke hente kvoter fra KMD.')
          }
        )
      })
    },
    parseQuotaItems (response) {
      let parsedData = response
      if (response && response.data && response.data.response && typeof response.data.response === 'string') {
        try {
          parsedData = JSON.parse(response.data.response)
        } catch (e) {
          return []
        }
      } else if (response && response.response && typeof response.response === 'string') {
        try {
          parsedData = JSON.parse(response.response)
        } catch (e) {
          return []
        }
      }

      let quotaItems = null
      if (parsedData && parsedData.Body && parsedData.Body.FetchQuotaListResponse_MT) {
        const quotaList = parsedData.Body.FetchQuotaListResponse_MT.OT_QUOTA_LIST
        if (quotaList) {
          quotaItems = typeof quotaList.item !== 'undefined' ? quotaList.item : quotaList.Item
        }
      }

      if (Array.isArray(quotaItems)) return quotaItems
      if (quotaItems && typeof quotaItems === 'object') return [quotaItems]
      return []
    },
    rowFromQuota (quota, pairing, index) {
      const user = pairing.user
      const emp = pairing.employment
      const title = emp.source_title || emp.title || ''
      const empNumber = emp.employment_number ? String(emp.employment_number) : ''
      const userTags = Array.isArray(user.user_tags) ? user.user_tags : []
      return {
        id: user.id + '_' + emp.id + '_' + (quota.QuotaNumber || quota.QuotaType || index || '0'),
        userId: user.id,
        userName: user.name,
        userTags: userTags,
        userTagsLabel: userTags.join(', '),
        employmentId: emp.id,
        employmentNumber: empNumber,
        employmentLabel: empNumber + (title ? ' - ' + title : ''),
        quotaType: quota.QuotaTypaText || quota.QuotaType || '',
        balance: quota.QuotaAccrued,
        used: quota.QuotaUsed,
        difference: quota.QuotaValue,
        periodLabel: this.formatDateRange(quota.DeductionStartDate, quota.DeductionEndDate)
      }
    },
    rowsFromQuotas (quotas, pairing) {
      const out = []
      for (let i = 0; i < quotas.length; i++) {
        out.push(this.rowFromQuota(quotas[i], pairing, i))
      }
      return out
    },
    attributeQuotas (quotas, pairings) {
      if (quotas.length === 0) return []
      if (pairings.length === 1) {
        return this.rowsFromQuotas(quotas, pairings[0])
      }
      const byNumber = {}
      for (let i = 0; i < pairings.length; i++) {
        const key = this.normalizeEmploymentNumber(pairings[i].employment.employment_number)
        if (key) byNumber[key] = pairings[i]
      }
      const rows = []
      for (let i = 0; i < quotas.length; i++) {
        const key = this.normalizeEmploymentNumber(this.quotaEmployeeNumber(quotas[i]))
        const pairing = key ? byNumber[key] : null
        if (!pairing) return null
        rows.push(this.rowFromQuota(quotas[i], pairing, i))
      }
      return rows
    },
    chunkArray (items, size) {
      const chunks = []
      for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size))
      }
      return chunks
    },
    fetchChunk (pairings) {
      const self = this
      const ids = pairings.map(function (pairing) { return pairing.employment.id })
      return this.listQuotas(ids).then(function (quotas) {
        const attributed = self.attributeQuotas(quotas, pairings)
        if (attributed) return attributed
        return self.fetchPairingsIndividually(pairings)
      }).catch(function () {
        return self.fetchPairingsIndividually(pairings)
      })
    },
    fetchPairingsIndividually (pairings) {
      const self = this
      const tasks = pairings.map(function (pairing) {
        return self.listQuotas([pairing.employment.id]).then(function (quotas) {
          return self.rowsFromQuotas(quotas, pairing)
        }).catch(function () {
          return null
        })
      })
      return Promise.all(tasks).then(function (groups) {
        const out = []
        let failed = 0
        for (let i = 0; i < groups.length; i++) {
          if (groups[i] == null) {
            failed += 1
          } else {
            out.push.apply(out, groups[i])
          }
        }
        if (failed > 0) {
          self.warningMessage = failed + ' ansættelser kunne ikke hentes fra KMD.'
        }
        return out
      })
    },
    fetchAllQuotas () {
      if (!this.hasSelection) return
      this.fetchGeneration += 1
      const generation = this.fetchGeneration
      const pairings = this.selectedOpusEmployments
      this.isLoading = true
      this.hasLoaded = false
      this.errorMessage = ''
      this.warningMessage = ''
      this.loadedCount = 0
      this.totalCount = pairings.length

      if (pairings.length === 0) {
        this.allRows = []
        this.isLoading = false
        this.hasLoaded = true
        return
      }

      const self = this
      const chunks = this.chunkArray(pairings, CHUNK_SIZE)
      let chain = Promise.resolve([])
      chunks.forEach(function (chunk) {
        chain = chain.then(function (rows) {
          if (generation !== self.fetchGeneration) return rows
          return self.fetchChunk(chunk).then(function (chunkRows) {
            if (generation !== self.fetchGeneration) return rows
            self.loadedCount += chunk.length
            return rows.concat(chunkRows)
          })
        })
      })
      chain.then(function (rows) {
        if (generation !== self.fetchGeneration) return
        self.allRows = rows
        self.isLoading = false
        self.hasLoaded = true
      }).catch(function (error) {
        if (generation !== self.fetchGeneration) return
        self.allRows = []
        self.errorMessage = typeof error === 'string' ? error : 'Kunne ikke hente kvoter fra KMD.'
        self.isLoading = false
        self.hasLoaded = true
      })
    },
    dataToExcel () {
      return this.filteredRows.map(row => ({
        user: row.userName || '',
        groups: row.userTagsLabel || '',
        employment: row.employmentLabel || '',
        quotaType: row.quotaType || '',
        balance: row.balance != null ? row.balance : '',
        used: row.used != null ? row.used : '',
        difference: row.difference != null ? row.difference : '',
        period: row.periodLabel || '',
        date: this.quotaDateLabel
      }))
    },
    downloadExcel () {
      if (this.isDownloadingExcel || this.filteredRows.length === 0) return
      this.isDownloadingExcel = true
      if (window.XLSX && XLSX) {
        this.writeExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.writeExcel()
          }
        }, 500)
      }
    },
    writeExcel () {
      const headers = {
        user: 'Navn',
        groups: 'Grupper',
        employment: 'Ansættelse',
        quotaType: 'Kontotype',
        balance: 'Saldo',
        used: 'Brugt',
        difference: 'Forskel',
        period: 'Afholdelsesperiode',
        date: 'Restkvote pr.'
      }
      const rows = this.dataToExcel()
      rows.unshift(headers)
      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, sheet, 'kvoter')
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      XLSX.writeFile(wb, 'kmd_kvoter_' + timestamp + '.xlsx')
      this.isDownloadingExcel = false
    }
  },
  mounted () {
    this.restoreFilters()
    this.$store.dispatch('readEmployments')
  },
  beforeDestroy () {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer)
      this.fetchTimer = null
    }
  }
}
</script>

<style scoped>
.d-modal {
  width: 1100px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  position: relative;
  background: #fff;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.d-modal__header h2 {
  margin: 0;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
  max-width: 52rem;
  line-height: 1.45;
}

.d-modal__date {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0;
}

.d-modal__date .d-label {
  margin: 0;
  white-space: nowrap;
}

.d-modal__select {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0;
}

.d-modal__select > div:first-child {
  flex: 1;
  min-width: 0;
}

.d-modal__select-all {
  flex-shrink: 0;
  margin-top: 2px;
}

.d-modal__all-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex: 1;
  min-height: 38px;
  padding: 0.5rem 0.75rem;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.d-modal__all-selected__clear {
  background: none;
  border: none;
  color: var(--primary, #468AD7);
  cursor: pointer;
  padding: 0;
  font-size: 13px;
}

.d-modal__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}

.d-modal__toolbar__filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  min-width: 0;
}

.d-modal__count {
  color: #6b7280;
  font-size: 13px;
}

.d-modal__content {
  padding: 1rem;
  max-height: calc(100vh - 220px);
  overflow: auto;
  overscroll-behavior: contain;
}

.d-modal__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  gap: 0.75rem;
}

.d-modal__progress,
.d-modal__empty {
  margin: 0;
  color: #71717a;
}

.d-modal__empty {
  font-style: italic;
  padding: 1.5rem 0;
  text-align: center;
}

.d-modal__error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.75rem 1rem;
}

.d-modal__warning {
  margin: 0.75rem 0 0;
  color: #854d0e;
  font-size: 13px;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f6f9fc;
}

.do-table tbody td {
  padding: 0.45rem 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.th-sorter {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.th-sorter--active {
  color: var(--primary, #468AD7);
}

.is-number {
  text-align: right;
}

@media (max-width: 800px) {
  .d-modal__toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>



// WEBPACK FOOTER //
// KmdQuotasModal.vue?7d9cee20